import type { SubmitHandler } from 'react-hook-form';
import clsx from 'clsx';
import { ToastManagerContext } from 'components/ToastManager';
import { LinkUnitEnv, useLinkUnitMutation } from 'hooks/internalApi';
import { useContextSafely } from 'hooks/useContextSafely';
import { useInternalApiContext } from 'hooks/useInternalApiContext';
import React, { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { getResultOrError } from 'utils/errors';
import { useWorkspaceId } from 'wrappers/WorkspaceProvider';

import { PrimaryButtonV2 } from '@fragment/ui/src/components/Button/PrimaryButtonV2/PrimaryButtonV2';
import { LinkIcon } from '@fragment/ui/src/components/Icon/LinkIcon/LinkIcon';
import { InlineError } from '@fragment/ui/src/components/InlineError/InlineError';
import { TextInput } from '@fragment/ui/src/components/Input/TextInput';
import { Toast } from '@fragment/ui/src/components/Toast';
import { Toggle } from '@fragment/ui/src/components/Toggle/Toggle';

import type { OnboardLinkFormProps } from './types';
import { DocsLink } from './DocsLink';

type FormData = {
  name: string;
  env: LinkUnitEnv;
  unitOrgId: string;
  orgToken: string;
};

export const OnboardUnitLinkForm = ({ onCreate }: OnboardLinkFormProps) => {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { isValid, errors },
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      env: LinkUnitEnv.Sandbox,
    },
  });
  const watchEnv = watch('env', LinkUnitEnv.Sandbox);

  const workspaceId = useWorkspaceId();
  const { context } = useInternalApiContext();
  const [, linkUnit] = useLinkUnitMutation();

  const { showToast } = useContextSafely(ToastManagerContext);
  const createUnitLink: SubmitHandler<FormData> = useCallback(
    async (data) => {
      const response = await linkUnit(
        {
          workspaceId,
          ...data,
        },
        context
      );
      const resultOrError = getResultOrError(
        response.data?.linkUnit,
        response.error
      );

      if (resultOrError.isError) {
        showToast(<Toast message={resultOrError.errorMessage} type="error" />);
      } else {
        await onCreate(resultOrError.result.link);
      }
    },
    [workspaceId, linkUnit, context, onCreate, showToast]
  );

  return (
    <form
      // eslint-disable-next-line @typescript-eslint/no-misused-promises -- grandfathered
      onSubmit={handleSubmit(createUnitLink)}
      data-testid="unit-onboarding-form"
    >
      <div className={clsx('space-y-f2')}>
        <div className={clsx('flex flex-row justify-between')}>
          <div className="flex items-center space-x-[2px]">
            <LinkIcon type="UnitLink" />
            <span>Unit</span>
          </div>
          <DocsLink linkType="UnitLink" />
        </div>
        <div className={clsx('space-y-f2')}>
          <TextInput
            data-testid="unit-onboarding-name-field"
            label="Name"
            size="md"
            padding="p-f0"
            width="w-full"
            flexContainer="space-y-0"
            // To keep backwards compatibility with the existing behavior
            placeholder={
              watchEnv === LinkUnitEnv.Production ? 'Unit' : 'Unit sandbox'
            }
            {...register('name', {
              required: {
                value: true,
                message: 'Enter a name for your Unit integration',
              },
              maxLength: {
                value: 128,
                message:
                  'Integration names should be shorter than 128 characters',
              },
            })}
          />
          {errors.name?.message && (
            <InlineError singleLine>{errors.name.message}</InlineError>
          )}
          <TextInput
            label="Org API Token"
            placeholder="Token"
            flexContainer="space-y-0"
            size="md"
            padding="p-f0"
            data-testid="unit-onboarding-orgToken-field"
            width="w-full"
            {...register('orgToken', {
              required: {
                value: true,
                message: 'Unit Org API token required',
              },
            })}
          />
          <span className="text-main-500">
            Note: requires accounts and transactions{' '}
            <a
              href="https://docs.unit.co/#scopes"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-main-500 transition-colors"
            >
              scopes
            </a>
          </span>
          {errors.orgToken?.message && (
            <InlineError singleLine>{errors.orgToken.message}</InlineError>
          )}
          <Toggle
            onLeftClick={() => setValue('env', LinkUnitEnv.Sandbox)}
            onRightClick={() => setValue('env', LinkUnitEnv.Production)}
            buttonHeight="h-f2"
            buttonPadding="px-f0"
            leftLabel="Sandbox"
            rightLabel="Production"
            initialActiveButton="left"
          />
          <PrimaryButtonV2 disabled={!isValid} type="submit">
            Add Link
          </PrimaryButtonV2>
        </div>
      </div>
    </form>
  );
};
